import { useEffect, useState } from 'react';
import { ArrowRight, Check, Gift, Lock } from 'lucide-react';
import { CATALOG_COURSES } from '../data/catalog';
import { submitStudentWaitlist } from '../utils/waitlist';

const WAITLIST_COURSE_OPTIONS = [
  ...CATALOG_COURSES.map((c) => c.title),
  'Intimacy Coordination Workshop',
  'Film Critique Workshop',
  'Film Festival Strategy Workshop',
  'Other',
];

const HEAR_ABOUT_OPTIONS = [
  'Instagram',
  'YouTube',
  'Friend or family',
  'School or college',
  'Google search',
  'Other',
];

/*
const FACULTY_INTEREST_OPTIONS = [
  { value: 'course', label: 'Teaching a course' },
  { value: 'workshop', label: 'Leading a workshop' },
  { value: 'curriculum', label: 'Creating curriculum' },
  { value: 'other', label: 'Other collaboration' },
];
*/

const WaitlistSection = ({
  chapterLabel = 'JOIN THE WAITLIST',
  sectionId,
  className = 'py-24 md:py-32 bg-black scroll-mt-24',
  defaultCourseInterest = '',
  defaultMessage = '',
}) => {
  const [studentForm, setStudentForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    course_interest: defaultCourseInterest,
    hear_about: '',
    message: defaultMessage,
  });
  const [studentSubmitted, setStudentSubmitted] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

  useEffect(() => {
    setStudentForm((prev) => ({
      ...prev,
      course_interest: defaultCourseInterest || prev.course_interest,
    }));
  }, [defaultCourseInterest]);

  useEffect(() => {
    if (!defaultMessage) return;
    setStudentForm((prev) => ({ ...prev, message: defaultMessage }));
  }, [defaultMessage]);

  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    const ok = await submitStudentWaitlist(studentForm, API);
    if (ok) {
      setStudentSubmitted(true);
      setStudentForm({
        name: '',
        email: '',
        phone: '',
        city: '',
        course_interest: defaultCourseInterest,
        hear_about: '',
        message: '',
      });
    }
  };

  return (
    <section id={sectionId} data-testid="waitlist-section" className={className}>
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="chapter-label mb-6 !inline-block">{chapterLabel}</p>
          <h2 className="display-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Be among the first<br />to start <em>learning.</em>
          </h2>
          <p className="text-base leading-relaxed text-zinc-400 max-w-xl mx-auto">
          Join the Storytelling / Feature film Direction course waitlist and we&apos;ll reach out when we launch
          </p>
        </div>

        {studentSubmitted ? (
          <div data-testid="student-waitlist-success" className="cine-card rounded-xl p-10 text-center">
            <Check className="w-10 h-10 text-[#C8102E] mx-auto mb-4" />
            <p className="font-serif text-2xl text-white mb-2">You&apos;re on the list.</p>
            <p className="text-sm text-zinc-400">We&apos;ll notify you when your course opens for enrolment.</p>
          </div>
        ) : (
          <form onSubmit={handleStudentSubmit} className="cine-card rounded-xl p-8 md:p-10 space-y-5 border border-white/5">
            <p className="text-sm text-zinc-400 mb-2">
              Tell us about yourself and which course or workshop you&apos;re interested in.
            </p>
            <input
              type="text"
              value={studentForm.name}
              onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
              placeholder="Full name"
              required
              data-testid="student-waitlist-name"
              className="w-full bg-black border border-white/10 text-white rounded-md px-5 py-4 focus:outline-none focus:border-[#A80D1A] transition-colors"
            />
            <input
              type="email"
              value={studentForm.email}
              onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
              placeholder="Email address"
              required
              data-testid="student-waitlist-email"
              className="w-full bg-black border border-white/10 text-white rounded-md px-5 py-4 focus:outline-none focus:border-[#A80D1A] transition-colors"
            />
            <input
              type="tel"
              value={studentForm.phone}
              onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })}
              placeholder="Phone number"
              required
              data-testid="student-waitlist-phone"
              className="w-full bg-black border border-white/10 text-white rounded-md px-5 py-4 focus:outline-none focus:border-[#A80D1A] transition-colors"
            />
            <input
              type="text"
              value={studentForm.city}
              onChange={(e) => setStudentForm({ ...studentForm, city: e.target.value })}
              placeholder="City"
              required
              data-testid="student-waitlist-city"
              className="w-full bg-black border border-white/10 text-white rounded-md px-5 py-4 focus:outline-none focus:border-[#A80D1A] transition-colors"
            />
            <select
              value={studentForm.course_interest}
              onChange={(e) => setStudentForm({ ...studentForm, course_interest: e.target.value })}
              required
              data-testid="student-waitlist-course"
              className="w-full bg-black border border-white/10 text-white rounded-md px-5 py-4 focus:outline-none focus:border-[#A80D1A] transition-colors"
            >
              <option value="" disabled>Course or workshop you&apos;re interested in</option>
              {WAITLIST_COURSE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <select
              value={studentForm.hear_about}
              onChange={(e) => setStudentForm({ ...studentForm, hear_about: e.target.value })}
              required
              data-testid="student-waitlist-hear-about"
              className="w-full bg-black border border-white/10 text-white rounded-md px-5 py-4 focus:outline-none focus:border-[#A80D1A] transition-colors"
            >
              <option value="" disabled>How did you hear about Cynopsis?</option>
              {HEAR_ABOUT_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <textarea
              value={studentForm.message}
              onChange={(e) => setStudentForm({ ...studentForm, message: e.target.value })}
              placeholder="Anything else you'd like us to know? (optional)"
              rows={3}
              data-testid="student-waitlist-message"
              className="w-full bg-black border border-white/10 text-white rounded-md px-5 py-4 focus:outline-none focus:border-[#A80D1A] transition-colors resize-none"
            />

            <div className="rounded-lg border border-white/10 bg-[#0D0D0D] p-5 flex gap-4">
              <Gift className="w-6 h-6 text-[#C8102E] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-semibold text-white mb-2">Early Waitlist Benefits</p>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Priority access &bull; Special launch offer &bull; Updates &bull; Free resources
                </p>
              </div>
            </div>

            <button
              type="submit"
              data-testid="student-waitlist-submit"
              className="btn-cine-primary w-full justify-center rounded-md"
            >
              Join Waitlist
              <ArrowRight className="w-4 h-4" />
            </button>

            <p className="flex items-center justify-center gap-2 text-xs text-zinc-500">
              <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
              We respect your privacy. No spam, ever.
            </p>
          </form>
        )}

        {/*
        Faculty waitlist tab and form — disabled for now.

        {waitlistTab === 'faculty' && (
          facultySubmitted ? (
            ...
          ) : (
            <form onSubmit={handleFacultySubmit}>...</form>
          )
        )}
        */}
      </div>
    </section>
  );
};

export default WaitlistSection;
